import React from "react";
import { Compass, Sparkles, ShieldCheck, Info } from "lucide-react";

export const PERSONAL_RADAR_PILLARS = {
  cardio: {
    title: "Pilar Cardiovascular",
    what: "Presión arterial sistólica y diastólica en reposo.",
    calculation: "PAS < 120 mmHg y PAD < 80 mmHg (Óptimo) • < 130/85 mmHg (Aceptable).",
    standard: "ACC/AHA 2017 & ESC/ESH 2018 Guidelines.",
    relevance: "Protección vascular y prevención de sobrecarga miocárdica.",
  },
  metabolic: {
    title: "Pilar Metabolismo & Composición",
    what: "Relación Cintura-Estatura (WHtR) y balance lipídico/glucémico.",
    calculation: "Índice Cintura/Talla (WHtR) ≤ 0.50.",
    standard: "IDF & Ashwell et al. 2012 Consensus.",
    relevance: "Ausencia de grasa visceral profunda y protección contra resistencia a la insulina.",
  },
  activity: {
    title: "Pilar Actividad & Movimiento",
    what: "Nivel de actividad física diaria y mitigación del sedentarismo laboral.",
    calculation: "Categoría IPAQ Moderado/Vigoroso o meta diaria de pasos alcanzada.",
    standard: "OMS Directrices Mundiales sobre Actividad Física 2020.",
    relevance: "Sensibilidad a la insulina, salud osteomuscular y activación mitocondrial.",
  },
  sleep: {
    title: "Pilar Recuperación & Sueño",
    what: "Horas continuas de descanso nocturno reparador.",
    calculation: "Duración de sueño nocturno continuado ≥ 7.0 horas.",
    standard: "National Sleep Foundation (NSF) & AASM Guidelines.",
    relevance: "Limpieza glinfática cerebral, consolidación de memoria y homeostasis inmune.",
  },
  emotional: {
    title: "Pilar Equilibrio Neurovegetativo",
    what: "Tono vagal y regulación de la variabilidad cardíaca (HRV) frente al estrés.",
    calculation: "Índice de Tensión de Baevsky ≤ 350 pts (telemetría rPPG / HRV).",
    standard: "Baevsky RM & Task Force ESC/NASPE 1996.",
    relevance: "Resiliencia ante la sobrecarga cognitiva y prevención del agotamiento laboral.",
  },
  respiratory: {
    title: "Pilar Eficiencia Pulmonar",
    what: "Saturación periférica de oxígeno (SpO2) en reposo.",
    calculation: "SpO2 transcutánea o rPPG ≥ 95%.",
    standard: "Consenso OMS / American Thoracic Society (ATS).",
    relevance: "Oxigenación celular tisular y adecuada ventilación alveolar.",
  },
};

/**
 * Calcula las 6 dimensiones del radar para un perfil individual
 */
export function calcIndividualRadarDimensions(profile = {}, latestMeasurement = {}, isDemo = false) {
  const p = profile || {};
  const vitals = latestMeasurement?.vitals || latestMeasurement?.fields || p.vitals || {};

  // Valores demo por defecto si la cuenta está marcada como demo
  if (isDemo) {
    const dims = [
      { key: "cardio", label: "Cardiovascular", score: 88, benchmark: 85, color: "#EF4444" },
      { key: "metabolic", label: "Metabolismo & Peso", score: 82, benchmark: 80, color: "#F97316" },
      { key: "activity", label: "Actividad & Movimiento", score: 78, benchmark: 75, color: "#10B981" },
      { key: "sleep", label: "Recuperación & Sueño", score: 80, benchmark: 80, color: "#3B82F6" },
      { key: "emotional", label: "Equilibrio Emocional", score: 84, benchmark: 85, color: "#8B5CF6" },
      { key: "respiratory", label: "Eficiencia Pulmonar", score: 96, benchmark: 90, color: "#06B6D4" },
    ];
    const avg = Math.round(dims.reduce((acc, d) => acc + d.score, 0) / dims.length);
    return { dimensions: dims, avgScore: avg, hasData: true };
  }

  // 1. Cardio
  let cardioScore = null;
  const pas = parseFloat(p.paSistolica || vitals.sbp || vitals.pas);
  const pad = parseFloat(p.paDiastolica || vitals.dbp || vitals.pad);
  if (!isNaN(pas) && pas > 0) {
    if (pas < 120 && (isNaN(pad) || pad < 80)) cardioScore = 95;
    else if (pas < 130 && (isNaN(pad) || pad < 85)) cardioScore = 85;
    else if (pas < 140 && (isNaN(pad) || pad < 90)) cardioScore = 65;
    else cardioScore = 45;
  }

  // 2. Metabolismo (WHtR o IMC)
  let metabolicScore = null;
  const cintura = parseFloat(p.cintura || p.clinical_measurements?.cintura);
  const talla = parseFloat(p.talla || p.height || p.clinical_measurements?.talla);
  if (!isNaN(cintura) && !isNaN(talla) && talla > 0) {
    const whtr = cintura / talla;
    if (whtr <= 0.48) metabolicScore = 95;
    else if (whtr <= 0.50) metabolicScore = 85;
    else if (whtr <= 0.55) metabolicScore = 65;
    else metabolicScore = 45;
  } else {
    const peso = parseFloat(p.peso || p.weight);
    if (!isNaN(peso) && !isNaN(talla) && talla > 0) {
      const imc = peso / Math.pow(talla / 100, 2);
      if (imc >= 18.5 && imc <= 24.9) metabolicScore = 90;
      else if (imc < 30) metabolicScore = 70;
      else metabolicScore = 45;
    }
  }

  // 3. Actividad
  let activityScore = null;
  const ipaq = String(p.f3Results?.category || p.f3Results?.categoria || p.lifestyle?.actividad || "").toLowerCase();
  if (ipaq) {
    if (ipaq.includes("high") || ipaq.includes("alto") || ipaq.includes("vigor")) activityScore = 95;
    else if (ipaq.includes("mod")) activityScore = 80;
    else if (ipaq.includes("low") || ipaq.includes("bajo") || ipaq.includes("sedentari")) activityScore = 50;
  }

  // 4. Sueño
  let sleepScore = null;
  const sueno = parseFloat(p.sueno || p.lifestyle?.sueno || p.f4Results?.sleepHours);
  if (!isNaN(sueno) && sueno > 0) {
    if (sueno >= 7.5) sleepScore = 95;
    else if (sueno >= 7.0) sleepScore = 85;
    else if (sueno >= 6.0) sleepScore = 65;
    else sleepScore = 45;
  }

  // 5. Emocional
  let emotionalScore = null;
  const stress = parseFloat(vitals.stressIndex || vitals.si || p.f5Results?.estresLaboralScore);
  if (!isNaN(stress)) {
    if (stress <= 200) emotionalScore = 95;
    else if (stress <= 350) emotionalScore = 85;
    else if (stress <= 500) emotionalScore = 65;
    else emotionalScore = 45;
  }

  // 6. Respiratorio
  let respiratoryScore = null;
  const spo2 = parseFloat(p.oximetria || vitals.spo2);
  if (!isNaN(spo2)) {
    if (spo2 >= 98) respiratoryScore = 98;
    else if (spo2 >= 95) respiratoryScore = 88;
    else if (spo2 >= 92) respiratoryScore = 65;
    else respiratoryScore = 45;
  }

  const scores = [cardioScore, metabolicScore, activityScore, sleepScore, emotionalScore, respiratoryScore];
  const validScores = scores.filter((s) => s !== null);
  const hasData = validScores.length >= 2;

  const dims = [
    { key: "cardio", label: "Cardiovascular", score: cardioScore ?? 0, benchmark: 85, color: "#EF4444" },
    { key: "metabolic", label: "Metabolismo & Peso", score: metabolicScore ?? 0, benchmark: 80, color: "#F97316" },
    { key: "activity", label: "Actividad & Movimiento", score: activityScore ?? 0, benchmark: 75, color: "#10B981" },
    { key: "sleep", label: "Recuperación & Sueño", score: sleepScore ?? 0, benchmark: 80, color: "#3B82F6" },
    { key: "emotional", label: "Equilibrio Emocional", score: emotionalScore ?? 0, benchmark: 85, color: "#8B5CF6" },
    { key: "respiratory", label: "Eficiencia Pulmonar", score: respiratoryScore ?? 0, benchmark: 90, color: "#06B6D4" },
  ];

  const avg = validScores.length > 0
    ? Math.round(validScores.reduce((acc, s) => acc + s, 0) / validScores.length)
    : 0;

  return { dimensions: dims, avgScore: avg, hasData };
}

export default function PersonalRadarCard({
  profile = {},
  latestMeasurement = {},
  isDemo = false,
  onNavigateToMeasurement,
  className = "",
}) {
  const radar = calcIndividualRadarDimensions(profile, latestMeasurement, isDemo);
  const { dimensions, avgScore, hasData } = radar;

  // Parámetros geométricos SVG
  const width = 360;
  const height = 300;
  const cx = 180;
  const cy = 150;
  const maxRadius = 100;
  const numAxes = dimensions.length;

  const getCoordinates = (index, value) => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / numAxes;
    const r = (Math.max(0, Math.min(100, value)) / 100) * maxRadius;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    return { x, y, angle };
  };

  const userPolygonPoints = dimensions
    .map((dim, idx) => {
      const { x, y } = getCoordinates(idx, hasData ? dim.score : 30);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const benchmarkPolygonPoints = dimensions
    .map((dim, idx) => {
      const { x, y } = getCoordinates(idx, dim.benchmark);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const rings = [25, 50, 75, 100];

  return (
    <div
      className={`bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-3xl p-6 shadow-sm relative overflow-hidden transition-all ${className}`}
      data-testid="personal-radar-card"
    >
      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-md bg-[#00628F]/10 text-[#00628F]">
              <Compass size={11} /> Tu Huella Biológica 360°
            </span>
            {hasData ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck size={11} /> 6 Dimensiones Calibradas
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                <Sparkles size={11} /> Calibración Inicial Pendiente
              </span>
            )}
          </div>
          <h3 className="text-xl font-extrabold text-slate-900 mt-1">
            Radar Holístico de Longevidad
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Balance integral de tus 6 pilares clínicos comparado con el estándar de longevidad óptimo.
          </p>
        </div>

        {/* Badge de Puntuación Media */}
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/80 rounded-2xl p-3 shrink-0">
          <div className="text-right">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">
              Índice 360°
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-[#00628F]" data-testid="radar-avg-score">
                {hasData ? avgScore : "--"}
              </span>
              <span className="text-[10px] text-slate-400 font-bold">/ 100</span>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center text-[#2A9D8F]">
            <Sparkles size={20} />
          </div>
        </div>
      </div>

      {/* Contenedor Gráfico SVG */}
      <div className="relative flex flex-col lg:flex-row items-center justify-center gap-6 my-2">
        <div className="relative w-full max-w-[360px] flex justify-center">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-auto overflow-visible select-none"
            aria-label="Gráfico de Radar Holístico Personal"
          >
            {/* Anillos concéntricos */}
            {rings.map((ringVal) => {
              const ringPoints = dimensions
                .map((_, idx) => {
                  const { x, y } = getCoordinates(idx, ringVal);
                  return `${x.toFixed(1)},${y.toFixed(1)}`;
                })
                .join(" ");

              return (
                <polygon
                  key={ringVal}
                  points={ringPoints}
                  fill={ringVal === 100 ? "#F8FAFC" : "none"}
                  stroke="#E2E8F0"
                  strokeWidth="1"
                  strokeDasharray={ringVal < 100 ? "2,2" : "none"}
                />
              );
            })}

            {/* Ejes radiales */}
            {dimensions.map((dim, idx) => {
              const { x, y } = getCoordinates(idx, 100);
              return (
                <line
                  key={dim.key}
                  x1={cx}
                  y1={cy}
                  x2={x}
                  y2={y}
                  stroke="#E2E8F0"
                  strokeWidth="1.2"
                />
              );
            })}

            {/* Polígono de Benchmark (Meta Óptima) */}
            <polygon
              points={benchmarkPolygonPoints}
              fill="rgba(148, 163, 184, 0.08)"
              stroke="#94A3B8"
              strokeWidth="1.5"
              strokeDasharray="4,4"
            />

            {/* Polígono del Usuario */}
            <polygon
              points={userPolygonPoints}
              fill={hasData ? "rgba(42, 157, 143, 0.22)" : "rgba(148, 163, 184, 0.1)"}
              stroke={hasData ? "#2A9D8F" : "#CBD5E1"}
              strokeWidth="2.5"
              strokeLinejoin="round"
              className="transition-all duration-700 ease-out"
            />

            {/* Puntos y Nodos */}
            {dimensions.map((dim, idx) => {
              const { x, y } = getCoordinates(idx, hasData ? dim.score : 30);
              return (
                <g key={dim.key} className="transition-transform duration-300">
                  <circle
                    cx={x}
                    cy={y}
                    r="4.5"
                    fill={hasData ? dim.color : "#94A3B8"}
                    stroke="#FFFFFF"
                    strokeWidth="2"
                  />
                </g>
              );
            })}

            {/* Etiquetas de los Ejes */}
            {dimensions.map((dim, idx) => {
              const { x, y, angle } = getCoordinates(idx, 118);
              const cosA = Math.cos(angle);
              let textAnchor = "middle";
              if (cosA > 0.3) textAnchor = "start";
              else if (cosA < -0.3) textAnchor = "end";

              return (
                <text
                  key={dim.key}
                  x={x}
                  y={y + 3}
                  textAnchor={textAnchor}
                  className="fill-slate-700 text-[10px] font-bold"
                >
                  {dim.label}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Desglose de los 6 Pilares a la derecha */}
        <div className="w-full lg:w-72 space-y-2">
          <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase pb-1 border-b border-slate-100">
            <span>Pilar Clínico</span>
            <span>Tu Nivel / Meta</span>
          </div>

          {dimensions.map((dim) => {
            const pillar = PERSONAL_RADAR_PILLARS[dim.key] || {};
            const isOptimal = dim.score >= dim.benchmark;

            return (
              <div
                key={dim.key}
                className="p-2.5 rounded-xl bg-slate-50/70 border border-slate-100 flex items-center justify-between transition-all hover:bg-slate-100/70"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: dim.color }}
                  />
                  <div>
                    <h5 className="text-xs font-bold text-slate-800 leading-none">
                      {dim.label}
                    </h5>
                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                      {pillar.title}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="flex items-center gap-1.5 justify-end">
                    <span className="text-xs font-extrabold text-slate-900">
                      {hasData ? `${dim.score}%` : "--"}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      / {dim.benchmark}%
                    </span>
                  </div>
                  <span
                    className={`text-[9px] font-bold uppercase tracking-wider ${
                      !hasData
                        ? "text-slate-400"
                        : isOptimal
                        ? "text-emerald-700"
                        : "text-amber-700"
                    }`}
                  >
                    {!hasData ? "Pendiente" : isOptimal ? "Óptimo" : "En Optimización"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Leyenda y Call to Action si falta calibración */}
      {!hasData && (
        <div className="mt-4 p-3.5 bg-gradient-to-r from-teal-50/80 to-blue-50/80 rounded-2xl border border-teal-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Info size={16} className="text-[#00628F] shrink-0" />
            <p className="text-xs text-slate-600">
              Completa tu <strong>Escaneo Facial rPPG</strong> o tus formularios clínicos para trazar tu huella biológica exacta.
            </p>
          </div>
          {onNavigateToMeasurement && (
            <button
              onClick={onNavigateToMeasurement}
              className="px-4 py-2 bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white text-xs font-bold rounded-xl shadow-sm hover:opacity-95 transition-all shrink-0 cursor-pointer"
            >
              Iniciar Escaneo
            </button>
          )}
        </div>
      )}

      {/* Footer con estándares de referencia */}
      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between text-[10px] text-slate-400 gap-2">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#2A9D8F]" /> Tu Estado Actual
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-0.5 bg-slate-400 inline-block border-t border-dashed" /> Meta de Longevidad (AHA/OMS)
          </span>
        </div>
        <span>Consenso ACC/AHA • IDF • OMS • Baevsky RM</span>
      </div>
    </div>
  );
}
